import { CONSTANTS } from '../game.js';
import { RandomGenerator } from '../utils/RandomGenerator.js';
import { ActiveTarget} from '../objects/ActiveTarget.js';
import { PredictiveTarget } from '../objects/PredictiveTarget.js';

export class TargetManager {
    /**
     * TargetManagerのコンストラクタ
     * @param {Phaser.Scene} scene - シーンオブジェクト
     * @param {number} seed - 乱数生成器のシード値
     */
    constructor(scene, seed) {
        this.scene = scene;
        this.activeTarget = null;
        this.predictiveTargets = [];
        
        // 専用の乱数生成器を初期化
        this.targetRNG = new RandomGenerator(seed);
        
        // アップデートイベントを登録
        this.scene.events.on('update', this.update, this);
    }
    
    /**
     * ターゲットの種類を決定する
     * @returns {string} ターゲットの種類
     */
    determineTargetType() {
        // 常にYELLOWタイプを返す
        return CONSTANTS.TARGET.TYPES.YELLOW;
    }
    
    /**
     * 移動方向をランダムに決定する
     * @returns {object} 移動方向 {x, y}
     */
    determineMovementDirection() {
        const directions = CONSTANTS.MOVING_TARGET.DIRECTIONS;
        const randomIndex = this.targetRNG.between(0, directions.length - 1);
        return directions[randomIndex];
    }

    /**
     * ランダムな位置を生成する
     * @returns {object} 位置情報 {x, y}
     */
    getRandomPosition() {
        return {
            x: this.targetRNG.between(
                CONSTANTS.TARGET.RADIUS + 30, 
                this.scene.game.config.width - (CONSTANTS.TARGET.RADIUS + 30)
            ),
            y: this.targetRNG.between(
                CONSTANTS.TARGET.RADIUS + 30, 
                this.scene.game.config.height - (CONSTANTS.TARGET.RADIUS + 30)
            )
        };
    }

    /**
     * 指定した数の的を作成
     * @param {number} count - 作成する的の数（アクティブ1つ + 予測的の数）
     */
    createTargets(count) {
        // 最低でも1つのアクティブターゲットと1つの予測ターゲットが必要
        const totalCount = Math.max(2, count);
        
        // アクティブターゲットを作成（順番は1から開始）
        const activePos = this.getRandomPosition();
        const activeType = this.determineTargetType();
        this.activeTarget = new ActiveTarget(this.scene, activePos.x, activePos.y, activeType, 1);
        
        // 50個目以降は動くターゲットとして設定
        if (1 >= CONSTANTS.MOVING_TARGET.START_COUNT) {
            this.initializeMovingTarget(this.activeTarget);
        }
        
        console.log('create ActiveTarget');
        
        // 予測ターゲットを作成
        for (let i = 0; i < totalCount - 1; i++) {
            const pos = this.getRandomPosition();
            const order = i + 2; // アクティブターゲットが1なので、2から始まる
            const targetType = this.determineTargetType();
            const predictiveTarget = new PredictiveTarget(this.scene, pos.x, pos.y, targetType, order);
            
            // 50個目以降は動くターゲットとして設定
            if (order >= CONSTANTS.MOVING_TARGET.START_COUNT) {
                this.initializeMovingTarget(predictiveTarget);
            }
            
            predictiveTarget.setNextOrder(order); // 表示用の順番を設定
            console.log('create PredictiveTarget');
            this.predictiveTargets.push(predictiveTarget);
        }
    }
    
    /**
     * 動的ターゲットの初期化
     * @param {AbstractTarget} target - 初期化するターゲット
     */
    initializeMovingTarget(target) {
        const direction = this.determineMovementDirection();
        const speed = CONSTANTS.MOVING_TARGET.SPEED;
        
        target.setMoving(true);
        target.setVelocity({
            x: direction.x * speed,
            y: direction.y * speed
        });
    }

    /**
     * 特定のターゲットを検索
     * @param {Matter.Body} targetBody - 検索する的の物理ボディ
     * @returns {ActiveTarget|PredictiveTarget|null} 見つかった的のオブジェクト、または null
     */
    findTarget(targetBody) {
        // アクティブターゲットをチェック
        if (this.activeTarget && this.activeTarget.getBody() === targetBody) {
            return this.activeTarget;
        }
        
        // 予測ターゲットをチェック
        return this.predictiveTargets.find(t => t.getBody() === targetBody) || null;
    }
    
    /**
     * アクティブなターゲットを取得
     * @returns {ActiveTarget|null} アクティブなターゲット、または null
     */
    getActiveTarget() {
        return this.activeTarget;
    }
    
    /**
     * 次のターゲットをアクティブにする
     */
    activateNextTarget() {
        if (this.predictiveTargets.length === 0) return;
        
        // 現在のアクティブターゲットを破棄
        if (this.activeTarget) {
            this.activeTarget.destroy();
        }
        
        // 最初の予測ターゲットをアクティブターゲットに変換
        const nextTarget = this.predictiveTargets.shift();
        const position = nextTarget.getPosition();
        const targetType = nextTarget.getTargetType();
        const order = nextTarget.getOrder(); // 順番を保持
        
        // 予測ターゲットを破棄
        nextTarget.destroy();
        
        // 新しいアクティブターゲットを作成（順番と色、移動状態を引き継ぐ）
        this.activeTarget = new ActiveTarget(
            this.scene,
            position.x,
            position.y,
            targetType,
            order,
            nextTarget.isMoving,
            nextTarget.velocity
        );
        
        // 新しい予測ターゲットを追加（最後の順番+1）
        const lastPredictive = this.predictiveTargets[this.predictiveTargets.length - 1];
        const newOrder = lastPredictive ? lastPredictive.getOrder() + 1 : order + 1;
        const newPos = this.getRandomPosition();
        const newTargetType = this.determineTargetType();
        const newPredictive = new PredictiveTarget(this.scene, newPos.x, newPos.y, newTargetType, newOrder);
        
        // 50個目以降は動くターゲットとして設定
        if (newOrder >= CONSTANTS.MOVING_TARGET.START_COUNT) {
            this.initializeMovingTarget(newPredictive);
        }
        
        newPredictive.setNextOrder(newOrder);
        this.predictiveTargets.push(newPredictive);
    }
    
    /**
     * ターゲットの更新処理
     */
    update() {
        // アクティブターゲットの更新
        if (this.activeTarget) {
            this.activeTarget.update();
        }
        
        // 予測ターゲットの更新
        this.predictiveTargets.forEach(target => {
            target.update();
        });
    }

    /**
     * 予測ターゲットの順番を更新
     */
    updatePredictiveTargetsOrder() {
        for (let i = 0; i < this.predictiveTargets.length; i++) {
            const target = this.predictiveTargets[i];
            const displayOrder = i + 1;
            target.setNextOrder(displayOrder);
        }
    }

    /**
     * 全てのターゲットを取得
     * @returns {Array<ActiveTarget|PredictiveTarget>} ターゲットの配列
     */
    getAllTargets() {
        return [this.activeTarget, ...this.predictiveTargets].filter(Boolean);
    }

    /**
     * リソースのクリーンアップ
     */
    cleanup() {
        // アップデートイベントの登録解除
        this.scene.events.off('update', this.update, this);
        
        // アクティブターゲットのクリーンアップ
        if (this.activeTarget) {
            this.activeTarget.destroy();
            this.activeTarget = null;
        }
        
        // 予測ターゲットのクリーンアップ
        if (this.predictiveTargets && this.predictiveTargets.length > 0) {
            this.predictiveTargets.forEach(target => {
                if (target && typeof target.destroy === 'function') {
                    target.destroy();
                }
            });
            this.predictiveTargets = [];
        }
    }
}
